import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, HelpCircle, ChevronDown, ChevronRight } from 'lucide-react';
import SEO from './SEO';

const HelpPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "How do I create a greeting card?",
      answer: "To create a greeting card, simply click on 'Templates' in the navigation, choose a template that you like, then customize it with your own text, colors, and styling. You can preview your card before finalizing it."
    },
    {
      question: "Do I need to create an account to use the service?",
      answer: "You need an account to create and save greeting cards. However, you can browse templates and explore the features without signing up. Creating an account is free and allows you to save your work and access premium features."
    },
    {
      question: "What subscription plans are available?",
      answer: "We offer flexible subscription plans that give you access to premium templates, advanced customization options, and high-resolution downloads. Visit our subscription page to see current pricing and features."
    },
    {
      question: "Can I download my greeting cards?",
      answer: "Yes! Once you've created your greeting card, you can download it in high-resolution format. Premium subscribers get access to additional download formats and higher quality options."
    },
    {
      question: "How do I share my greeting cards?",
      answer: "After creating your card, you'll get a shareable link that you can send to friends and family. They can view your card online without needing an account."
    },
    {
      question: "Can I edit a card after I've created it?",
      answer: "Yes, if you're logged in, you can access your saved cards from your profile and edit them at any time. Your changes will be automatically saved."
    },
    {
      question: "What image formats are supported for uploads?",
      answer: "We support common image formats including JPG, PNG, and GIF. For best results, we recommend using high-resolution images."
    },
    {
      question: "Is there a limit to how many cards I can create?",
      answer: "Free users can create a limited number of cards per month. Premium subscribers enjoy unlimited card creation along with other exclusive benefits."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "You can cancel your subscription at any time from your profile page under 'Manage Subscription'. Your premium features will remain active until the end of your current billing period."
    },
    {
      question: "Are my cards private?",
      answer: "Your cards are private by default. Only people with the direct link you share can view them. We never share your personal cards publicly without your permission."
    }
  ];

  return (
    <>
      <SEO
        title="Help Center & FAQ"
        description="Get help with DreamableAI. Find answers to frequently asked questions about creating greeting cards, subscriptions, sharing, and more. Contact our support team."
        path="/help"
      />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/" className="text-4xl font-bold text-purple-600 hover:text-purple-700 transition">
              🎨 DreamableAI
            </Link>
            <h1 className="mt-6 text-3xl font-bold text-gray-900">Help Center</h1>
            <p className="mt-2 text-lg text-gray-600">
              Find answers to your questions and get support
            </p>
          </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Support</h3>
            <p className="text-gray-600 mb-4">Get direct help from our support team</p>
            <a
              href="mailto:info@hungryai.com"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              info@hungryai.com
            </a>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">FAQ</h3>
            <p className="text-gray-600 mb-4">Browse frequently asked questions</p>
            <button
              onClick={() => document.getElementById('faq-section').scrollIntoView({ behavior: 'smooth' })}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              View FAQ Below
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Feature Request</h3>
            <p className="text-gray-600 mb-4">Suggest new features or improvements</p>
            <a
              href="mailto:info@hungryai.com"
              className="text-green-600 hover:text-green-700 font-medium"
            >
              info@hungryai.com
            </a>
          </div>
        </div>

        {/* Legal & Policy Links */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Legal & Privacy</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to="/terms"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition group"
            >
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 group-hover:text-purple-700">Terms of Service</h3>
                <p className="text-sm text-gray-600 mt-1">Our terms and conditions for using DreamableAI</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-purple-600" />
            </Link>
            
            <Link
              to="/privacy"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition group"
            >
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 group-hover:text-purple-700">Privacy Policy</h3>
                <p className="text-sm text-gray-600 mt-1">How we collect, use, and protect your data</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-purple-600" />
            </Link>
          </div>
        </div>

        {/* FAQ Section */}
        <div id="faq-section" className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <div className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Still need help?</p>
          <div className="space-x-4">
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 transition"
            >
              ← Back to DreamableAI
            </Link>
            <a
              href="mailto:info@hungryai.com"
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition"
            >
              Contact Support
            </a>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default HelpPage; 